import { useState, useEffect, useCallback } from 'react'
import { adminApi } from '../../api/adminApi'
import { Modal } from '../ui/Modal'
import { Spinner } from '../ui/Spinner'
import { Button } from '../ui/Button'
import type { EmailLogEntry, PaginatedResponse } from '../../types'

const PAGE_SIZE = 50

const TYPE_LABELS: Record<string, string> = {
  SubscriptionConfirmation: 'Confirm',
  WelcomeAfterConfirm:      'Welcome',
  UnsubscribeConfirmation:  'Unsub',
  NewPostNotification:      'Post Alert',
}

const STATUS_COLORS: Record<string, string> = {
  delivered:      '#16a34a',
  failed:         '#dc2626',
  outForDelivery: '#d97706',
  queued:         '#6b7280',
  unknown:        '#9ca3af',
}

function StatusDot({ status }: { status: string }) {
  const color = STATUS_COLORS[status] ?? '#9ca3af'
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%',
        background: color, display: 'inline-block', flexShrink: 0,
      }} />
      <span style={{ color, fontSize: '0.8rem', fontWeight: 500 }}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    </span>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

interface Props {
  open: boolean
  onClose: () => void
}

export function EmailLogsModal({ open, onClose }: Props) {
  const [data,    setData]    = useState<PaginatedResponse<EmailLogEntry> | null>(null)
  const [page,    setPage]    = useState(1)
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState<string | null>(null)

  const load = useCallback(async (p: number) => {
    setLoading(true)
    setError(null)
    try {
      const result = await adminApi.getEmailLogs(p, PAGE_SIZE)
      setData(result)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load email logs')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open) load(page)
  }, [open, page, load])

  const entries     = data?.data ?? []
  const totalPages  = data?.meta.totalPages ?? 1
  const totalCount  = data?.meta.totalCount ?? 0

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Email Logs"
      maxWidth={900}
    >
      <div className="el-wrap">
        {loading && !data ? (
          <div className="el-center"><Spinner label="Loading…" /></div>
        ) : error ? (
          <div className="el-center el-error">{error}</div>
        ) : (
          <>
            <p className="el-summary">
              {totalCount.toLocaleString()} email{totalCount !== 1 ? 's' : ''} sent in total
            </p>

            <div className="el-table-wrap">
              <table className="el-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Type</th>
                    <th>To</th>
                    <th>Subject</th>
                    <th>Sent</th>
                    <th>Last Checked</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="el-empty">No emails sent yet.</td>
                    </tr>
                  ) : entries.map((e) => (
                    <tr key={e.id}>
                      <td><StatusDot status={e.status} /></td>
                      <td>
                        <span className="el-type-badge">
                          {TYPE_LABELS[e.emailType] ?? e.emailType}
                        </span>
                      </td>
                      <td className="el-monospace">{e.toAddress}</td>
                      <td className="el-subject" title={e.subject}>{e.subject}</td>
                      <td className="el-date">{formatDate(e.sentAt)}</td>
                      <td className="el-date">
                        {e.lastCheckedAt ? formatDate(e.lastCheckedAt) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="el-pagination">
                <span className="el-page-info">
                  Page {page} of {totalPages}
                </span>
                <div className="el-page-btns">
                  <Button size="sm" variant="secondary"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >← Prev</Button>
                  <Button size="sm" variant="secondary"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >Next →</Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        .el-wrap { display: flex; flex-direction: column; gap: 16px; }
        .el-center { display: flex; justify-content: center; padding: 48px 0; }
        .el-error { color: var(--color-error); font-size: 0.875rem; }
        .el-summary { font-size: 0.8rem; color: var(--text-muted); }
        .el-table-wrap { overflow-x: auto; border: 1px solid var(--border); border-radius: var(--radius-md); }
        .el-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.825rem;
          min-width: 700px;
        }
        .el-table thead th {
          padding: 10px 14px;
          text-align: left;
          font-size: 0.7rem;
          font-weight: 700;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          background: var(--bg-surface);
          border-bottom: 1px solid var(--border);
          white-space: nowrap;
        }
        .el-table tbody tr { border-bottom: 1px solid var(--border-light); }
        .el-table tbody tr:last-child { border-bottom: none; }
        .el-table tbody tr:hover { background: var(--bg-hover); }
        .el-table td { padding: 10px 14px; vertical-align: middle; }
        .el-empty { text-align: center; color: var(--text-muted); padding: 32px !important; }
        .el-type-badge {
          padding: 2px 8px;
          border-radius: 99px;
          font-size: 0.72rem;
          font-weight: 600;
          background: var(--bg-hover);
          color: var(--text-secondary);
          white-space: nowrap;
        }
        .el-monospace {
          font-family: var(--font-mono);
          font-size: 0.78rem;
          color: var(--text-secondary);
        }
        .el-subject {
          max-width: 220px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: var(--text-primary);
        }
        .el-date { white-space: nowrap; color: var(--text-muted); font-size: 0.78rem; }
        .el-pagination {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-top: 4px;
          flex-wrap: wrap;
          gap: 8px;
        }
        .el-page-info { font-size: 0.78rem; color: var(--text-muted); }
        .el-page-btns { display: flex; gap: 8px; }
      `}</style>
    </Modal>
  )
}
